/**
 * Add / edit one replaceable part. Each type has its own fields; "Other" and saved custom types use spec rows.
 * No price or link fields: the Replace-it card finds today's price and link from brand + model + store.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Camera, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { prepareImageFileForUpload } from "@/lib/prepareImageForUpload";
import { usePhotoGroups } from "@/hooks/usePhotoGroups";
import { usePropertyRooms } from "@/hooks/usePropertyRooms";
import {
  useCreateCustomPartType,
  useCreatePart,
  useCustomPartTypes,
  useDeletePart,
  useUpdatePart,
} from "@/hooks/usePropertyParts";
import {
  PART_TYPES,
  SPEC_LABEL_SUGGESTIONS,
  otherRows,
  partTypeDef,
  type OtherRow,
  type PartTypeId,
  type PropertyPart,
} from "@/lib/parts";
import { sameRoom } from "@/lib/photoGroups";
import type { Appliance } from "@/hooks/useAppliances";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle } from "@/components/ui/drawer";
import { useIsMobile } from "@/hooks/use-mobile";
import { ReplaceItDialog } from "@/components/owner/ReplaceItDialog";

const REPLACE_OPTIONS: { label: string; months: number | null }[] = [
  { label: "When out", months: null },
  { label: "3 mo", months: 3 },
  { label: "6 mo", months: 6 },
  { label: "1 yr", months: 12 },
  { label: "2 yr", months: 24 },
];

function Chip({ label, on, onClick }: { label: string; on: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "flex min-h-[42px] items-center rounded-[12px] px-3.5 text-[14px] font-bold",
        on ? "bg-[#15776d] text-white" : "bg-[#f4f7f9] text-[#0f1720] hover:bg-[#e9eff2]",
      )}
    >
      {label}
    </button>
  );
}

function Label({ children }: { children: React.ReactNode }) {
  return <div className="mb-1.5 text-[11px] font-bold tracking-[0.8px] text-[#8494a2]">{children}</div>;
}

const inputCls =
  "h-11 w-full rounded-[12px] border-[1.5px] border-[#e6edf1] bg-white px-3.5 text-[15px] font-medium text-[#0f1720] outline-none focus:border-[#24a89a]";

export function PartDialog({
  open,
  onClose,
  propertyId,
  part,
  roomNames,
  appliances,
}: {
  open: boolean;
  onClose: () => void;
  propertyId: string;
  part: PropertyPart | null;
  roomNames: string[];
  appliances: Appliance[];
}) {
  const isMobile = useIsMobile();
  const { data: groups = [] } = usePhotoGroups(propertyId);
  const { data: roomRows = [] } = usePropertyRooms(propertyId);
  const { data: customTypes = [] } = useCustomPartTypes();
  const create = useCreatePart(propertyId);
  const update = useUpdatePart(propertyId);
  const remove = useDeletePart(propertyId);
  const createType = useCreateCustomPartType();

  const [type, setType] = useState<PartTypeId>("bulb");
  const [customTypeId, setCustomTypeId] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [room, setRoom] = useState("");
  const [place, setPlace] = useState("");
  const [spec, setSpec] = useState<Record<string, string>>({});
  const [rows, setRows] = useState<OtherRow[]>([{ label: "", value: "" }]);
  const [quantity, setQuantity] = useState(1);
  const [brand, setBrand] = useState("");
  const [model, setModel] = useState("");
  const [store, setStore] = useState("");
  const [photoLabel, setPhotoLabel] = useState<string | null>(null);
  const [photoPlace, setPhotoPlace] = useState<string | null>(null);
  const [replaceMonths, setReplaceMonths] = useState<number | null>(null);
  const [lastReplaced, setLastReplaced] = useState("");
  const [canReplace, setCanReplace] = useState(false);
  const [keepSpares, setKeepSpares] = useState(1);
  const [applianceId, setApplianceId] = useState("");
  const [busy, setBusy] = useState(false);
  const [replaceOpen, setReplaceOpen] = useState(false);
  const [saveTypeName, setSaveTypeName] = useState<string | null>(null);
  const labelRef = useRef<HTMLInputElement>(null);
  const placeRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    setType((part?.part_type as PartTypeId) ?? "bulb");
    setCustomTypeId(part?.custom_type_id ?? null);
    setName(part?.name ?? "");
    setRoom(part?.room ?? roomNames[0] ?? "");
    setPlace(part?.place ?? "");
    const sp = (part?.spec ?? {}) as Record<string, unknown>;
    const flat: Record<string, string> = {};
    for (const [k, v] of Object.entries(sp)) if (k !== "rows" && v != null) flat[k] = String(v);
    setSpec(flat);
    const r = otherRows(sp);
    setRows(r.length ? r : [{ label: "", value: "" }]);
    setQuantity(part?.quantity ?? 1);
    setBrand(part?.brand ?? "");
    setModel(part?.model ?? "");
    setStore(part?.store ?? "");
    setPhotoLabel(part?.photo_label_url ?? null);
    setPhotoPlace(part?.photo_place_url ?? null);
    setReplaceMonths(part?.replace_every_months ?? null);
    setLastReplaced(part?.last_replaced ? part.last_replaced.slice(0, 7) : "");
    setCanReplace(part?.cleaner_can_replace ?? false);
    setKeepSpares(part?.keep_spares ?? 1);
    setApplianceId(part?.appliance_id ?? "");
    setSaveTypeName(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, part?.id]);

  const def = partTypeDef(type);
  const customDef = customTypeId ? customTypes.find((t) => t.id === customTypeId) : null;
  const roomSpots = useMemo(() => {
    const rr = roomRows.find((r) => sameRoom(r.name, room));
    return rr ? groups.filter((g) => g.room_id === rr.id) : [];
  }, [groups, roomRows, room]);

  const upload = async (file: File, which: "label" | "place") => {
    setBusy(true);
    try {
      const prepared = await prepareImageFileForUpload(file);
      const path = `${propertyId}/parts/${crypto.randomUUID()}.jpg`;
      const { error } = await supabase.storage.from("reference-photos").upload(path, prepared, {
        contentType: "image/jpeg",
        upsert: false,
      });
      if (error) throw error;
      const url = supabase.storage.from("reference-photos").getPublicUrl(path).data.publicUrl;
      if (which === "label") setPhotoLabel(url);
      else setPhotoPlace(url);
    } catch (err) {
      toast({ title: "Upload failed", description: (err as { message?: string })?.message, variant: "destructive" });
    } finally {
      setBusy(false);
    }
  };

  const save = async () => {
    const finalName =
      name.trim() ||
      (type === "other" ? "" : `${def.label}${place.trim() ? ` · ${place.trim()}` : ""}`) ||
      "Part";
    if (type === "other" && !name.trim()) {
      toast({ title: "Name required", variant: "destructive" });
      return;
    }
    const specOut: Record<string, unknown> = {};
    if (type === "other") {
      specOut.rows = rows.filter((r) => r.label.trim() || r.value.trim()).slice(0, 4);
    } else {
      for (const f of def.fields) if (spec[f.key]) specOut[f.key] = spec[f.key];
    }
    const payload = {
      part_type: type,
      custom_type_id: type === "other" ? customTypeId : null,
      name: finalName,
      room: room || null,
      place: place.trim() || null,
      spec: specOut,
      quantity: Math.max(1, quantity),
      brand: brand.trim() || null,
      model: model.trim() || null,
      store: store.trim() || null,
      photo_label_url: photoLabel,
      photo_place_url: photoPlace,
      replace_every_months: replaceMonths,
      last_replaced: lastReplaced ? `${lastReplaced}-01` : null,
      cleaner_can_replace: canReplace,
      keep_spares: Math.max(0, keepSpares),
      appliance_id: applianceId || null,
    };
    setBusy(true);
    try {
      if (part) await update.mutateAsync({ id: part.id, ...payload });
      else await create.mutateAsync(payload);
      toast({ title: "Saved" });
      if (type === "other" && !customTypeId && (specOut.rows as OtherRow[]).length > 0) {
        setSaveTypeName(finalName);
      } else {
        onClose();
      }
    } catch (err) {
      toast({ title: "Couldn’t save", description: (err as { message?: string })?.message, variant: "destructive" });
    } finally {
      setBusy(false);
    }
  };

  const doRemove = async () => {
    if (!part) return;
    if (!window.confirm(`Delete ${part.name}? Its spare supply line goes too.`)) return;
    setBusy(true);
    try {
      await remove.mutateAsync(part);
      onClose();
    } catch (err) {
      toast({ title: "Couldn’t delete", description: (err as { message?: string })?.message, variant: "destructive" });
    } finally {
      setBusy(false);
    }
  };

  const photoSlot = (url: string | null, ref: React.RefObject<HTMLInputElement>, label: string, which: "label" | "place") => (
    <>
      <input
        ref={ref}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          e.target.value = "";
          if (f) void upload(f, which);
        }}
      />
      <button
        type="button"
        onClick={() => ref.current?.click()}
        className={cn(
          "relative flex h-[92px] flex-1 items-center justify-center overflow-hidden rounded-[14px]",
          url ? "bg-[#eef2f5]" : "border-2 border-dashed border-[#cfdbe2] bg-[#f8fafb] text-[#15776d]",
        )}
      >
        {url ? (
          <>
            <img src={url} alt="" className="h-full w-full object-cover" />
            <span className="absolute bottom-1.5 left-1.5 rounded-md bg-white/90 px-1.5 py-0.5 text-[11px] font-bold text-[#475569]">{label}</span>
          </>
        ) : (
          <span className="flex flex-col items-center gap-1 text-center text-[12.5px] font-bold">
            <Camera className="h-4 w-4" />
            {label}
          </span>
        )}
      </button>
    </>
  );

  const body = (
    <div className="flex flex-col gap-4 pb-2">
      {part && saveTypeName === null ? (
        <button
          type="button"
          onClick={() => setReplaceOpen(true)}
          className="flex h-11 items-center justify-center rounded-[12px] bg-[#e2eeed] text-[14.5px] font-bold text-[#15776d]"
        >
          Replace it: what to buy
        </button>
      ) : null}
      {saveTypeName !== null ? (
        <div className="flex flex-col gap-3 rounded-[18px] bg-[#e8f4f1] p-4">
          <div className="text-[17px] font-bold text-[#0d453f]">Save as a new part type?</div>
          <div className="text-[13.5px] font-medium text-[#3b6a63]">
            Next time, {saveTypeName.toLowerCase()} gets its own fields: {rows.filter((r) => r.label.trim()).map((r) => r.label).join(", ") || "spec rows"}.
          </div>
          <input value={saveTypeName} onChange={(e) => setSaveTypeName(e.target.value)} className={inputCls} aria-label="Part type name" />
          <div className="flex gap-2.5">
            <button type="button" onClick={onClose} className="h-11 flex-1 rounded-[12px] bg-white text-[14.5px] font-bold text-[#475569]">
              Not now
            </button>
            <button
              type="button"
              disabled={busy || !saveTypeName.trim()}
              onClick={async () => {
                try {
                  await createType.mutateAsync({ name: saveTypeName.trim(), spec_labels: rows.map((r) => r.label.trim()).filter(Boolean) });
                  toast({ title: "Part type saved" });
                } catch (err) {
                  toast({ title: "Couldn’t save the type", description: (err as { message?: string })?.message, variant: "destructive" });
                }
                onClose();
              }}
              className="h-11 flex-[1.4] rounded-[12px] bg-[#15776d] text-[14.5px] font-bold text-white disabled:opacity-60"
            >
              Save type
            </button>
          </div>
        </div>
      ) : (
        <>
          <div>
            <Label>TYPE</Label>
            <div className="flex flex-wrap gap-2">
              {PART_TYPES.map((t) => (
                <Chip
                  key={t.id}
                  label={t.label}
                  on={type === t.id && !customTypeId}
                  onClick={() => {
                    setType(t.id);
                    setCustomTypeId(null);
                  }}
                />
              ))}
              {customTypes.map((ct) => (
                <Chip
                  key={ct.id}
                  label={ct.name}
                  on={customTypeId === ct.id}
                  onClick={() => {
                    setType("other");
                    setCustomTypeId(ct.id);
                    setRows(ct.spec_labels.length ? ct.spec_labels.map((l) => ({ label: l, value: "" })) : [{ label: "", value: "" }]);
                  }}
                />
              ))}
            </div>
          </div>

          <div className="flex gap-2.5">
            {photoSlot(photoLabel, labelRef, "Label close-up", "label")}
            {photoSlot(photoPlace, placeRef, "In place", "place")}
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <Label>NAME</Label>
              <input value={name} onChange={(e) => setName(e.target.value)} placeholder={type === "other" ? "Shower head" : `${def.label}, e.g. Pot lights`} className={inputCls} />
            </div>
            <div>
              <Label>ROOM</Label>
              <select value={room} onChange={(e) => setRoom(e.target.value)} className={inputCls}>
                {roomNames.map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <Label>WHERE EXACTLY</Label>
            <input value={place} onChange={(e) => setPlace(e.target.value)} placeholder="Over the island" className={inputCls} />
            {roomSpots.length > 0 ? (
              <div className="mt-2 flex flex-wrap gap-2">
                {[...new Set(roomSpots.map((g) => g.name))].slice(0, 6).map((n) => (
                  <Chip key={n} label={n} on={place === n} onClick={() => setPlace(n)} />
                ))}
              </div>
            ) : null}
          </div>

          {type !== "other" ? (
            <div className="flex flex-col gap-3.5">
              {def.fields.map((f) => (
                <div key={f.key}>
                  <Label>{f.label.toUpperCase()}</Label>
                  {f.options ? (
                    <div className="flex flex-wrap gap-2">
                      {f.options.map((o) => (
                        <Chip key={o} label={o} on={spec[f.key] === o} onClick={() => setSpec((s) => ({ ...s, [f.key]: s[f.key] === o ? "" : o }))} />
                      ))}
                      {f.free ? (
                        <input
                          value={f.options.includes(spec[f.key] ?? "") ? "" : spec[f.key] ?? ""}
                          onChange={(e) => setSpec((s) => ({ ...s, [f.key]: e.target.value }))}
                          placeholder="Other"
                          aria-label={`${f.label} other`}
                          className="h-[42px] w-28 rounded-[12px] border-[1.5px] border-[#e6edf1] px-3 text-[14px] font-medium outline-none focus:border-[#24a89a]"
                        />
                      ) : null}
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <input
                        value={spec[f.key] ?? ""}
                        onChange={(e) => setSpec((s) => ({ ...s, [f.key]: e.target.value }))}
                        placeholder={f.placeholder}
                        className={cn(inputCls, f.unit && "max-w-[160px]")}
                      />
                      {f.unit ? <span className="text-[14px] font-bold text-[#64748b]">{f.unit}</span> : null}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div>
              <Label>SPEC ROWS · UP TO 4</Label>
              <div className="flex flex-col gap-2">
                {rows.map((r, i) => (
                  <div key={i} className="flex gap-2">
                    <input
                      list="spec-label-suggestions"
                      value={r.label}
                      onChange={(e) => setRows((rs) => rs.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)))}
                      placeholder="Label"
                      aria-label="Spec label"
                      className={cn(inputCls, "w-[42%]")}
                    />
                    <input
                      value={r.value}
                      onChange={(e) => setRows((rs) => rs.map((x, j) => (j === i ? { ...x, value: e.target.value } : x)))}
                      placeholder="Value"
                      aria-label="Spec value"
                      className={inputCls}
                    />
                  </div>
                ))}
              </div>
              <datalist id="spec-label-suggestions">
                {SPEC_LABEL_SUGGESTIONS.map((s) => (
                  <option key={s} value={s} />
                ))}
              </datalist>
              {rows.length < 4 ? (
                <button type="button" onClick={() => setRows((rs) => [...rs, { label: "", value: "" }])} className="mt-2 text-[14px] font-bold text-[#15776d]">
                  + Add a row
                </button>
              ) : null}
            </div>
          )}

          <div className="flex items-center justify-between gap-3 rounded-[14px] bg-[#f4f7f9] px-4 py-3">
            <div className="text-[15px] font-bold text-[#0f1720]">How many installed</div>
            <div className="flex items-center gap-2.5">
              <button type="button" onClick={() => setQuantity((q) => Math.max(1, q - 1))} className="flex h-10 w-10 items-center justify-center rounded-[12px] bg-white text-[20px] font-bold">
                −
              </button>
              <span className="w-8 text-center text-[18px] font-bold tabular-nums">{quantity}</span>
              <button type="button" onClick={() => setQuantity((q) => q + 1)} className="flex h-10 w-10 items-center justify-center rounded-[12px] bg-white text-[20px] font-bold">
                +
              </button>
            </div>
          </div>

          <div className="grid gap-3 sm:grid-cols-3">
            <div>
              <Label>BRAND</Label>
              <input value={brand} onChange={(e) => setBrand(e.target.value)} placeholder="Philips" className={inputCls} />
            </div>
            <div>
              <Label>MODEL</Label>
              <input value={model} onChange={(e) => setModel(e.target.value)} placeholder="9290011369" className={inputCls} />
            </div>
            <div>
              <Label>STORE</Label>
              <input value={store} onChange={(e) => setStore(e.target.value)} placeholder="Home Depot" className={inputCls} />
            </div>
          </div>
          <div className="text-[12.5px] font-medium text-[#8494a2]">No price or link is saved. The Replace-it card looks up today’s.</div>

          <div>
            <Label>REPLACE EVERY · OPTIONAL</Label>
            <div className="flex flex-wrap gap-2">
              {REPLACE_OPTIONS.map((o) => (
                <Chip key={o.label} label={o.label} on={replaceMonths === o.months} onClick={() => setReplaceMonths(o.months)} />
              ))}
            </div>
          </div>
          <div>
            <Label>LAST REPLACED</Label>
            <input type="month" value={lastReplaced} onChange={(e) => setLastReplaced(e.target.value)} className={cn(inputCls, "max-w-[220px]")} />
          </div>
          {appliances.length > 0 ? (
            <div>
              <Label>BELONGS TO · OPTIONAL</Label>
              <select value={applianceId} onChange={(e) => setApplianceId(e.target.value)} className={inputCls}>
                <option value="">Nothing</option>
                {appliances.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name}
                  </option>
                ))}
              </select>
            </div>
          ) : null}

          <div className="rounded-[14px] bg-[#f4f7f9] p-4">
            <button type="button" onClick={() => setCanReplace((v) => !v)} aria-pressed={canReplace} className="flex w-full items-center justify-between gap-3 text-left">
              <span>
                <span className="block text-[15px] font-bold text-[#0f1720]">Cleaner can replace</span>
                <span className="block text-[12.5px] font-medium text-[#64748b]">Puts a spare on the supply list</span>
              </span>
              <span className={cn("flex h-8 w-14 shrink-0 items-center rounded-full p-1 transition-colors", canReplace ? "bg-[#15776d]" : "bg-[#cdd8df]")}>
                <span className={cn("h-6 w-6 rounded-full bg-white transition-transform", canReplace ? "translate-x-6" : "translate-x-0")} />
              </span>
            </button>
            {canReplace ? (
              <div className="mt-3 flex items-center justify-between gap-3 border-t border-[#e6edf1] pt-3">
                <div className="text-[14px] font-bold text-[#0f1720]">Keep at least</div>
                <div className="flex items-center gap-2.5">
                  <button type="button" onClick={() => setKeepSpares((q) => Math.max(0, q - 1))} className="flex h-10 w-10 items-center justify-center rounded-[12px] bg-white text-[20px] font-bold">
                    −
                  </button>
                  <span className="w-8 text-center text-[18px] font-bold tabular-nums">{keepSpares}</span>
                  <button type="button" onClick={() => setKeepSpares((q) => q + 1)} className="flex h-10 w-10 items-center justify-center rounded-[12px] bg-white text-[20px] font-bold">
                    +
                  </button>
                  <span className="text-[13px] font-medium text-[#64748b]">spares in the closet</span>
                </div>
              </div>
            ) : null}
          </div>

          <div className="flex items-center gap-3 pt-1">
            {part ? (
              <button type="button" onClick={() => void doRemove()} className="px-1 py-2.5 text-[14.5px] font-bold text-[#b4453a]">
                Delete
              </button>
            ) : null}
            <div className="flex-1" />
            <button type="button" onClick={onClose} className="flex h-12 items-center rounded-[12px] bg-[#f1f5f8] px-5 text-[15px] font-bold text-[#64748b]">
              Cancel
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => void save()}
              className="flex h-12 items-center rounded-[12px] bg-[#15776d] px-7 text-[15px] font-bold text-white disabled:opacity-50"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
            </button>
          </div>
        </>
      )}
    </div>
  );

  const title = part ? part.name : "Add part";
  const dialog = isMobile ? (
    <Drawer open={open} onOpenChange={(o) => !o && onClose()}>
      <DrawerContent className="max-h-[94vh] px-4 pb-8">
        <DrawerHeader>
          <DrawerTitle>{title}</DrawerTitle>
        </DrawerHeader>
        <div className="overflow-y-auto">{body}</div>
      </DrawerContent>
    </Drawer>
  ) : (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        {body}
      </DialogContent>
    </Dialog>
  );
  return (
    <>
      {dialog}
      {replaceOpen && part ? <ReplaceItDialog part={part} onClose={() => setReplaceOpen(false)} /> : null}
    </>
  );
}
